<template>
  <div ref="inputRef" class="add-dish-or-ingredient">
    <input
      ref="search"
      class="search"
      placeholder="Wpisz nazwę"
      :value="searchValue"
      :data-test-id="`${props.testId}_AddDishOrIngredientInput`"
      @input="searchHandler($event)"
      @click.stop.prevent
    >
    <div
      ref="options"
      class="options"
      :style="optionsStyle"
      @scroll="scrollHandler"
      @keydown="focusSearch"
    >
      <template
        v-if="
          mode !== 'ingredient' &&
            unusedPortionsExist &&
            status === 'success'
        "
      >
        <div class="unused-portions-title">Niewykorzystane porcje:</div>
        <div
          v-for="[hash, dish] in unusedPortions"
          :key="hash"
          class="option"
          tabindex="0"
          @keyup.enter="selectUnusedDishPortion(dish)"
          @click="selectUnusedDishPortion(dish)"
        >
          <div class="unused"><BaseIcon size="12" name="cached" /></div>
          <div class="name">{{ dish.name }}</div>
          <div class="properties">
            <div class="portions">{{ dish.maxPortions }}P</div>
            <div v-if="!dish.isPublic" class="private"><BaseIcon size="12" name="lock" /></div>
          </div>
        </div>
        <div class="separator" />
      </template>

      <Space v-if="status === 'error'" align-y="center" :padding-y="10">
        <T as="span" size="sm">Nie udało się pobrać wyników</T>
        <StyledButton size="small" @click.stop.prevent="refetch">Spróbuj ponownie</StyledButton>
      </Space>
      <div v-else-if="isPending" class="loading">
        <BaseLoader class="base-loader--size-12" />
        <div class="loading-name">Ładowanie wyników</div>
      </div>
      <div v-else-if="searchResults.length === 0" class="no-results">Brak wyników</div>
      <template v-else>
        <div
          v-for="dishOrIngredient in searchResults"
          :key="`${dishOrIngredient.type}-${dishOrIngredient.id}`"
          class="option"
          tabindex="0"
          @keyup.enter="
            selectDishOrIngredient(
              $event,
              dishOrIngredient.id,
              dishOrIngredient.type,
              dishOrIngredient.name
            )
          "
          @click="
            selectDishOrIngredient(
              $event,
              dishOrIngredient.id,
              dishOrIngredient.type,
              dishOrIngredient.name
            )
          "
        >
          <div class="type"><BaseIcon size="12" :name="getTypeIcon(dishOrIngredient.type)" /></div>
          <div class="name" :title="dishOrIngredient.name">{{ dishOrIngredient.name }}</div>
          <div class="properties">
            <div
              v-if="'max_portions' in dishOrIngredient && dishOrIngredient.max_portions > 1"
              class="portions"
            >
              {{ dishOrIngredient.max_portions }}
              P
            </div>
            <div v-if="!dishOrIngredient.is_public" class="private">
              <BaseIcon size="12" name="lock" />
            </div>
            <div v-if="dishOrIngredient.is_favourite" class="favourite">
              <BaseIcon size="12" name="star" />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { A } from '@mobily/ts-belt'
  import { onClickOutside, useEventListener, useResizeObserver } from '@vueuse/core'
  import { storeToRefs } from 'pinia'
  import { computed, onMounted, ref, toRef } from 'vue'

  import { T } from '@ui/T'

  import BaseIcon from '@/components/BaseIcon.vue'
  import BaseLoader from '@/components/BaseLoader.vue'
  import { Space } from '@/components/Space'
  import { StyledButton } from '@/components/StyledButton'
  import { useFuseSearch } from '@/hooks/useFuseSearch'
  import { useSearchDishesAndIngredientsSimple } from '@/hooks/useSearchDishesAndIngredients'
  import { useDietsStore } from '@/store/dietsStore'
  import { useGlobalStore } from '@/store/globalStore'
  import { type DishSearchFilters, type RelatedDish } from '@/types/Diet'
  import { getDropdownPosition } from '@/utils/dropdownPosition'
  import { reportError } from '@/utils/reportError'

  type Props = {
    mode: 'all' | 'dish' | 'ingredient'
    testId: string
    dishMealType?: number
    filters?: DishSearchFilters
    relatedDishes?: Record<string, RelatedDish>
  }
  const props = defineProps<Props>()
  const emit = defineEmits(['select', 'select-unused', 'close'])

  const options = ref<HTMLDivElement>()
  const search = ref<HTMLInputElement>()
  const relatedDishes = toRef(props, 'relatedDishes')
  const mode = toRef(props, 'mode')
  const dishMealType = toRef(props, 'dishMealType')
  const filters = toRef(props, 'filters')

  const inputRef = ref<HTMLDivElement>()
  const searchValue = ref('')
  const searchLimit = ref(50)
  const unusedPortions = ref<[string, RelatedDish][]>([])
  const unusedPortionsExist = ref(false)
  const optionsStyle = ref({ left: '0px', width: '400px' })
  const updateOptionsPosition = () => {
    if (!inputRef.value) return
    const { left, width } = getDropdownPosition(inputRef.value)
    optionsStyle.value = { left: `${left}px`, width: `${width}px` }
  }

  useEventListener(window, 'resize', updateOptionsPosition)
  useEventListener(window, 'scroll', updateOptionsPosition, { capture: true, passive: true })
  useEventListener(window.visualViewport, 'resize', updateOptionsPosition)
  useEventListener(window.visualViewport, 'scroll', updateOptionsPosition)
  useResizeObserver(
    computed(() => [inputRef.value, inputRef.value?.closest<HTMLElement>('.app-content')]),
    updateOptionsPosition,
  )

  const dietStore = useDietsStore()
  const globalStore = useGlobalStore()
  const { user } = storeToRefs(globalStore)
  const { sendProductToFile } = dietStore
  const { status, data, isPending, refetch } = useSearchDishesAndIngredientsSimple(filters)

  const fuseSearchResult = useFuseSearch(data, searchValue, ['name'])

  const searchResults = computed(() => {
    const dishesAndIngredients = [...fuseSearchResult.value]

    dishesAndIngredients.sort((a, b) => {
      // sort by name
      const aName = a.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/\u0142/g, 'l')
      const bName = b.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/\u0142/g, 'l')
      if (aName < bName) return -1
      if (aName > bName) return 1
      return 0
    })
    dishesAndIngredients.sort((a, b) => {
      // sort by type
      if (a.type < b.type) return -1
      if (a.type > b.type) return 1
      return 0
    })
    dishesAndIngredients.sort((a, b) => {
      // sort by favourite
      return -1 * (Number(a.is_favourite) - Number(b.is_favourite))
    })
    dishesAndIngredients.sort((a, b) => {
      // sort favourites by name
      if (a.is_favourite && b.is_favourite && a.type === b.type) {
        const aName = a.name
          .toLowerCase()
          .normalize('NFD')
          .replace(/\u0142/g, 'l')
        const bName = b.name
          .toLowerCase()
          .normalize('NFD')
          .replace(/\u0142/g, 'l')
        if (aName < bName) return -1
        if (aName > bName) return 1
      }
      return 0
    })

    return dishesAndIngredients
      .filter(dishOrIngredient => {
        let check = true
        if (mode.value !== 'all') {
          check = dishOrIngredient.type === mode.value
        }
        if (
          check &&
          dishMealType.value &&
          'meal_types_ids' in dishOrIngredient &&
          A.isNotEmpty(dishOrIngredient.meal_types_ids)
        ) {
          check = dishOrIngredient.meal_types_ids.includes(dishMealType.value)
        }
        return check
      })
      .slice(0, Math.min(searchLimit.value, fuseSearchResult.value.length))
  })

  const searchHandler = (event: Event) => {
    searchLimit.value = 50
    searchValue.value = (event.target as HTMLInputElement).value
  }

  const getTypeIcon = (type: 'dish' | 'ingredient') => {
    if (type === 'dish') return 'restaurant'
    return 'local_grocery_store'
  }

  const scrollHandler = ({ target }: Event) => {
    const { scrollTop, clientHeight, scrollHeight } = target as HTMLDivElement

    if (Math.ceil(scrollTop + clientHeight) >= scrollHeight * 0.95) {
      searchLimit.value += 50
    }
  }

  const focusSearch = (event: KeyboardEvent) => {
    if (event.key !== 'Tab' && event.key !== 'Shift' && event.key !== 'Enter') {
      search.value?.focus()
    }
  }

  const updateUnusedPortions = () => {
    if (relatedDishes.value) {
      unusedPortions.value = Object.entries(relatedDishes.value)
        .filter(([, dish]) => dish.unusedPortions > 0)
        .sort(([, dish1], [, dish2]) => {
          const dish1Name = dish1.name
            .toLowerCase()
            .normalize('NFD')
            .replace(/\u0142/g, 'l')
          const dish2Name = dish2.name
            .toLowerCase()
            .normalize('NFD')
            .replace(/\u0142/g, 'l')
          if (dish1Name < dish2Name) return -1
          if (dish1Name > dish2Name) return 1
          return 0
        })
      if (Object.keys(unusedPortions.value).length > 0) unusedPortionsExist.value = true
    }
  }

  const selectDishOrIngredient = async (
    _: Event,
    dishOrIngredientId: number,
    dishOrIngredientType: 'dish' | 'ingredient',
    dishOrIngredientName: string,
  ) => {
    emit('select', {
      dishOrIngredientId,
      dishOrIngredientType,
    })
    if (dishOrIngredientType === 'ingredient' && user?.value) {
      await sendProductToFile({
        email: user.value.email,
        productId: dishOrIngredientId,
        productName: dishOrIngredientName,
      })
    }
  }

  const selectUnusedDishPortion = (dish: RelatedDish) => {
    if (dish.coordinates[0]?.[0]) {
      emit('select-unused', {
        dayIndex: dish.coordinates[0][0].dayIndex,
        dishOrIngredientIndex: dish.coordinates[0][0].dishOrIngredientIndex,
        mealIndex: dish.coordinates[0][0].mealIndex,
      })
    }
  }

  onMounted(async () => {
    search.value?.focus()
    updateOptionsPosition()
    updateUnusedPortions()

    if (status.value === 'error') {
      try {
        await refetch()
      } catch (err) {
        reportError(err, 'Error while fetching dishes and ingredients')
      }
    }
  })

  onClickOutside(inputRef, () => {
    emit('close')
  })
</script>

<style scoped>
  .add-dish-or-ingredient {
    position: relative;
  }

  .search {
    box-sizing: border-box;
    width: 100%;
    height: 32px;
    padding: 7px 8px;
    font-size: 14px;
    line-height: 18px;
    outline: 0;
    background: var(--color-main-10);
    border: 0;
    border-bottom: 1px solid var(--color-main-100);
    border-radius: 4px 4px 0 0;
  }

  .search::placeholder {
    font-size: 12px;
    color: var(--color-tertiary-text);
  }

  .options {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 100;
    width: 400px;
    max-height: 400px;
    padding: 0;
    overflow: hidden auto;
    scrollbar-width: thin;
    background: #fff;
    box-shadow: 0 1px 6px 0 rgb(155 155 155 / 50%);
  }

  .options::-webkit-scrollbar {
    width: 6px;
  }

  .unused-portions-title {
    padding: 4px;
    font-family: Montserrat-Medium;
    font-size: 12px;
    pointer-events: none;
    user-select: none;
  }

  .separator {
    width: calc(100% + 12px);
    height: 1px;
    margin-left: -6px;
    background: var(--color-tertiary-text);
  }

  .loading {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: left;
    box-sizing: border-box;
    width: 100%;
    height: 24px;
    padding: 0 6px;
    font-size: 12px;
    color: var(--color-secondary-text);
    pointer-events: none;
    user-select: none;
  }

  .loading-name {
    margin-left: 4px;
  }

  .loading .base-loader__circle::before {
    background-color: var(--color-secondary-text);
  }

  .no-results {
    box-sizing: border-box;
    width: 100%;
    height: 24px;
    padding: 0 4px;
    font-size: 12px;
    line-height: 24px;
    color: var(--color-secondary-text);
    pointer-events: none;
    user-select: none;
  }

  .option {
    z-index: 101;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: stretch;
    width: 100%;
    height: 24px;
    padding: 0 8px;
    font-size: 12px;
    cursor: pointer;
  }

  .option:hover,
  .option:focus {
    outline: none;
    background: var(--color-main-10);
  }

  .type {
    display: inline-flex;
    pointer-events: none;
  }

  .type .base-icon {
    color: var(--color-main-100);
    pointer-events: none;
  }

  .unused .base-icon {
    color: var(--color-destructive-100);
  }

  .name {
    flex-grow: 1;
    margin: 0 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .properties {
    flex-shrink: 0;
    display: flex;
    flex-direction: row;
    gap: 2px;
    align-items: center;
    margin-right: 4px;
  }

  .properties div {
    display: flex;
    align-items: center;
  }

  .portions {
    font-family: Montserrat-Bold;
    font-size: 10px;
    color: var(--color-main-100);
  }

  .private .base-icon,
  .favourite .base-icon {
    color: var(--color-tertiary-text);
  }
</style>
